import { useState } from 'react';
import Button from '@/components/Button';
import ConfirmModal from '@/components/ConfirmModal';
import SamplesPlayer from '@/components/SamplesPlayer';
import { MAX_VOICE_SELECTION } from '@shared/constants';
import type { AiVoice } from '@shared/types';
import styles from './ResultsScreen.module.css';

export interface VoiceMatch {
  voice: AiVoice;
  score: number;
  scorePercent: number;
}

export default function ResultsScreen({
  matches,
  submitting,
  error,
  onConfirm,
}: {
  matches: VoiceMatch[];
  submitting: boolean;
  error: string | null;
  onConfirm: (selectedVoiceIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  function toggle(voiceId: string) {
    setSelected((prev) => {
      if (prev.includes(voiceId)) return prev.filter((id) => id !== voiceId);
      if (prev.length >= MAX_VOICE_SELECTION) return prev;
      return [...prev, voiceId];
    });
  }

  function handleConfirm() {
    if (selected.length === 0) return;
    setConfirmOpen(true);
  }

  function handleConfirmFinal() {
    setConfirmOpen(false);
    onConfirm(selected);
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <h2>
          {matches.length} Stimme{matches.length === 1 ? '' : 'n'} für dich
        </h2>
        <p>
          Höre rein und wähle bis zu {MAX_VOICE_SELECTION} Favoriten.
          Kleiner Prozentwert bedeutet nicht „schlecht" – nur „passt weniger
          zu deinen konkreten Antworten".
        </p>
      </div>

      <div className={styles.cards}>
        {matches.map((m) => (
          <VoiceResultCard
            key={m.voice.elevenlabs_id}
            match={m}
            selected={selected.includes(m.voice.elevenlabs_id)}
            atLimit={
              !selected.includes(m.voice.elevenlabs_id) &&
              selected.length >= MAX_VOICE_SELECTION
            }
            onToggle={() => toggle(m.voice.elevenlabs_id)}
          />
        ))}
      </div>

      <div className={styles.bottomBar}>
        <div className={styles.counter}>
          <strong>{selected.length}</strong> / {MAX_VOICE_SELECTION} gewählt
          {error && (
            <span style={{ color: 'var(--color-error)', marginLeft: 16 }}>
              Fehler: {error}
            </span>
          )}
        </div>
        <Button
          onClick={handleConfirm}
          disabled={selected.length === 0 || submitting}
        >
          {submitting ? 'Schließe ab…' : 'Voice-Auswahl abschließen'}
        </Button>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title="Auswahl abschließen?"
        message="Möchtest du die Auswahl final abschließen? Danach ist dieses Casting beendet."
        confirmLabel="Abschließen"
        onConfirm={handleConfirmFinal}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function VoiceResultCard({
  match,
  selected,
  atLimit,
  onToggle,
}: {
  match: VoiceMatch;
  selected: boolean;
  atLimit: boolean;
  onToggle: () => void;
}) {
  const { voice, scorePercent } = match;
  const cls = [
    styles.card,
    selected ? styles.selected : '',
    atLimit ? styles.atLimit : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className={styles.cardMain}>
        <div className={styles.cardHeader}>
          <h3 className={styles.voiceName}>{voice.name}</h3>
          <span className={styles.scoreBadge}>{scorePercent}% Match</span>
        </div>
        <div className={styles.voiceMeta}>
          {[voice.gender, voice.age_range].filter(Boolean).join(' · ')}
          {voice.languages.length > 0 && ` · ${voice.languages.join(', ')}`}
        </div>
        <SamplesPlayer voiceId={voice.elevenlabs_id} />
      </div>
      <div className={styles.toggleWrap}>
        <Button
          active={selected}
          onClick={onToggle}
          disabled={atLimit && !selected}
        >
          {selected ? 'Gewählt ✓' : 'Wählen'}
        </Button>
      </div>
    </div>
  );
}
